// Compare places — two or three of them, on one page.
//
// Built like the briefing (city/panel.ts): the markup is strings from
// lib/compare-view.ts, and this file decides only when to fetch, where the
// strings go, and the few listeners a string cannot carry.
//
// The page's state is `state.compare`: the places (one to three) and which of
// the two documents is showing. One place is a comparison waiting for its
// second — that is what the tray at the bottom-left holds while a reader goes
// looking. The date, the rating and the time of day are the app's own, so the
// ruler and the sentence's words move this page exactly as they move a briefing.
//
// Every place's year comes from the same /city/:id/series the briefing reads —
// no new endpoint — fetched in parallel and cached by (id, bucket).

import { $, $maybe } from '../dom.ts';
import { map } from '../map/instance.ts';
import { state, store } from '../state.ts';
import type { ComparePlace } from '../state.ts';
import { MOBILE, TABLET } from '../ui/breakpoints.ts';
import { anchorTo, menuRow } from '../ui/sentence.ts';
import { openSearch } from '../ui/search-menu.ts';
import { setComparePins } from '../map/layers.ts';
import { isLight } from '../map/theme.ts';
import { computeCustomSeries } from '../city/panel.ts';
import { track } from '../analytics.ts';
import { VAR } from '../../lib/variables.ts';
import type { VarKey } from '../../lib/variables.ts';
import { sectionLabel } from '../../lib/sections.ts';
import { escapeHtml } from '../../lib/city-view.ts';
import { comparePath, MAX_COMPARE } from '../../lib/slug.ts';
import {
  whoLeadsHtml, verdictStripHtml, whereOf, overlayChart, OVERLAYS, dotPlotHtml, legendHtml, listNames,
  mastheadsHtml, goodWhenHtml, headToHeadHtml, sideBySideCharts,
} from '../../lib/compare-view.ts';
import type { CitySeries, CityHit } from '../../contracts/city.ts';

export interface CompareDeps {
  setView: (v: 'landing' | 'map' | 'city' | 'compare') => void;
  persist: () => void;
  showFrame: (idx: number) => void;
  pause: () => void;
  applyVariable: (v: VarKey) => void;
  activityKeys: () => VarKey[];
  openCity: (id: number, hint: CityHit | ComparePlace) => void;
  copyLink: (btn: HTMLElement) => void;
  quietMap: (on: boolean) => void;
}

let deps: CompareDeps;

export function initCompare(d: CompareDeps): void {
  deps = d;
}

const cache = new Map<string, CitySeries>();
let token = 0;
// The loaded series, in the order of state.compare.places. Null until they land.
let loaded: CitySeries[] | null = null;
// Which place's name opened the place menu.
let menuIdx = 0;

const light = () => isLight();

export const placeFrom = (p: CityHit | ComparePlace | { id: number; name: string; country: string; admin?: string; placeSlug?: string; lat?: number; lon?: number }): ComparePlace => ({
  id: p.id,
  name: p.name,
  country: p.country,
  admin: p.admin,
  placeSlug: p.placeSlug || '',
  lat: p.lat ?? 0,
  lon: p.lon ?? 0,
});

// ── opening, adding, leaving ─────────────────────────────────────────────────

// Show a comparison. Fewer than two places is not a page yet: it becomes the
// tray's pending pick and the search opens for the next.
export async function openCompare(places: ComparePlace[], { full, animate = true }: { full?: boolean; animate?: boolean } = {}): Promise<void> {
  const uniq: ComparePlace[] = [];
  for (const p of places) if (!uniq.some((q) => q.id === p.id)) uniq.push(p);
  const list = uniq.slice(0, MAX_COMPARE);
  store.set({ compare: { places: list, full: full ?? state.compare.full } });
  if (list.length < 2) {
    renderTray();
    openSearch('compare');
    return;
  }
  track('compare_open', { n: list.length });
  store.set({ menu: null });
  deps.setView('compare');
  deps.quietMap(true);
  frame(animate);
  await load();
}

// A place picked from the search panel (or the tray's "+ one more").
export function addToComparison(hit: CityHit): void {
  const p = placeFrom(hit);
  const places = state.compare.places.filter((q) => q.id !== p.id);
  if (places.length >= MAX_COMPARE) places.pop();
  places.push(p);
  if (state.view === 'compare' || places.length >= 2) {
    openCompare(places);
  } else {
    store.set({ compare: { places, full: state.compare.full } });
    renderTray();
  }
}

// A row's "+ Compare": in or out of the comparison being gathered. Never opens
// the page by itself — the tray's "Compare →" does, once there are two. A
// fourth place pushes out the first, so the latest picks are the ones kept.
export function toggleInComparison(p: ComparePlace): void {
  const places = state.compare.places;
  let next = places.some((q) => q.id === p.id) ? places.filter((q) => q.id !== p.id) : [...places, p];
  if (next.length > MAX_COMPARE) next = next.slice(next.length - MAX_COMPARE);
  store.set({ compare: { places: next, full: state.compare.full } });
  renderTray();
}

// "Compare Split with…": the briefing's word, and the corner word with a city open.
export function startCompareFrom(p: ComparePlace | null): void {
  const places = p ? [p] : state.view === 'compare' ? state.compare.places : state.compare.places.slice(0, 1);
  store.set({ compare: { places, full: state.compare.full } });
  renderTray();
  openSearch('compare');
}

function removePlace(i: number): void {
  const places = state.compare.places.filter((_, k) => k !== i);
  store.set({ compare: { places, full: state.compare.full }, menu: null });
  if (places.length >= 2) {
    frame(true);
    load();
  } else {
    startCompareFrom(places[0] || null);
  }
}

// Fetch every place's year, then draw. Cached per (id, bucket), so a switch of
// document, lens or date redraws from what is in hand.
export async function load(): Promise<void> {
  const t = ++token;
  const places = state.compare.places;
  renderSentenceNames();
  if (!loaded || loaded.length !== places.length || loaded.some((s, i) => s.id !== places[i].id || s.bucket !== state.bucket)) {
    $('cmpbody').innerHTML = '<div class="extras-desc city-wait">Loading the places…</div>';
  }
  try {
    const list = await Promise.all(places.map(async (p) => {
      const key = `${p.id}|${state.bucket}`;
      let s = cache.get(key);
      if (!s) {
        const res = await fetch(`/city/${p.id}/series?bucket=${state.bucket}`);
        if (!res.ok) throw new Error(String(res.status));
        s = (await res.json()) as CitySeries;
        cache.set(key, s);
      }
      return s;
    }));
    if (t !== token) return;
    // Fill in what only the series knew — a place opened by slug knows its
    // coordinates, one picked from a list may not.
    const filled = places.map((p, i) => ({ ...p, admin: list[i].admin, lat: list[i].lat, lon: list[i].lon, placeSlug: p.placeSlug || list[i].placeSlug || '' }));
    store.set({ compare: { places: filled, full: state.compare.full } });
    for (const s of list) computeCustomSeries(s);
    loaded = list;
    renderCompare();
    frame(false);
  } catch {
    if (t === token) $('cmpbody').innerHTML = '<div class="extras-desc city-wait">Couldn’t load these places.</div>';
  }
  deps.persist();
}

// The band frames every place, clear of the sentence above and the strip below.
function frame(animate: boolean): void {
  const pts = state.compare.places.filter((p) => p.lat || p.lon);
  setComparePins(state.view === 'compare' ? pts.map((p, k) => ({ lon: p.lon, lat: p.lat, name: p.name, k })) : [], light());
  if (state.view !== 'compare' || pts.length < 2) return;
  const lons = pts.map((p) => p.lon), lats = pts.map((p) => p.lat);
  map.resize();
  const mobile = MOBILE.matches;
  map.fitBounds(
    [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
    // Between the sentence above and the verdict strip below — the pins may
    // sit behind the strip's top edge, never behind the sentence.
    { padding: mobile ? { top: 40, bottom: 30, left: 40, right: 40 } : { top: 150, bottom: 130, left: 160, right: 160 }, maxZoom: 6, duration: animate ? 700 : 0 },
  );
}

// Leaving the view takes its pins off the map.
export function onLeaveCompare(): void {
  deps.quietMap(false);
  setComparePins([], light());
  renderTray();
}

// ── drawing ──────────────────────────────────────────────────────────────────

// What the page is about, when: the showing rating, or the first activity when
// a measurement is showing (the leader strip and the columns need a rating).
function activeRating(): VarKey | null {
  return VAR[state.variable]?.score ? state.variable : null;
}

export function renderCompare(): void {
  renderSentenceNames();
  if (state.view !== 'compare' || !loaded) return;
  const list = loaded;
  const dates = state.dates;
  const i = state.dateIdx;
  const v = state.variable;
  const acts = deps.activityKeys();
  const wide = !MOBILE.matches && !TABLET.matches;
  const when = sectionLabel(dates[i]);
  // The verdicts lead the document rather than riding the map: over the map
  // they had to fight the sentence, the pins and the ground for the same pixels.
  const strip = verdictStripHtml(list, v, i, dates, state.recipe, { compact: MOBILE.matches, why: state.whyExpanded });
  const lead = `<div class="cmp-strip${MOBILE.matches ? ' cmp-strip-m' : ''}">${strip}</div>`;

  let body: string;
  if (!state.compare.full) {
    // "At a glance", where every section can be opened on its own (3b): a
    // rating row into its places, a chart into one per place, the dots into a
    // table. What is open is the reader's (state.compareUi), kept like "Why?".
    const ui = state.compareUi;
    const allOpen = acts.every((a) => ui.open.includes(a));
    const splitBtn = (id: string) => {
      const on = ui.split.includes(id);
      return `<button type="button" class="cmp-disclose" data-split="${id}" aria-pressed="${on}">${on ? 'one axis' : 'one chart per place'}</button>`;
    };
    const chartBody = (spec: typeof OVERLAYS[number], wideChart: boolean, H: number) => ui.split.includes(spec.id)
      ? `<div class="sbs-grid${wideChart ? '' : ' sbs-stack'}" style="grid-template-columns:repeat(${wideChart ? list.length : 1},minmax(0,1fr))">${sideBySideCharts(list, spec.split, dates, i)}</div>`
      : `<div class="chart-svg cmp-chart${wideChart && wide ? ' cmp-wide' : ''}">${overlayChart(list, spec, dates, { curIdx: i, wide: wideChart && wide, H })}</div>`;
    const [temp, ...small] = OVERLAYS;
    const smallCards = small.map((spec, k) =>
      `<section class="dcard cg-${'abc'[k]}"><div class="dcard-head"><span class="sec-title">${spec.title}</span><span class="sec-unit">${spec.unit}</span>${splitBtn(spec.id)}</div>`
      + chartBody(spec, false, 220) + `</section>`).join('');
    const rated = activeRating();
    const readings = ui.table
      ? headToHeadHtml(list, v, i, dates) + `<p class="cmp-note">${rated ? `Best for ${escapeHtml(VAR[rated].label.toLowerCase())} lit. ` : ''}The dots and the table are the same readings.</p>`
      : dotPlotHtml(list, i, dates);
    body = lead
      + `<section class="cmp-sec">`
      + `<div class="dcard-head"><span class="sec-title">Who leads, when</span><span class="sec-unit">each ten-day section coloured by the place that rates highest · pick a rating to see every place under it</span>`
      + `<button type="button" class="cmp-disclose" data-cl-all="${allOpen ? 'close' : 'open'}">${allOpen ? 'Close all' : `Open all ${acts.length === 5 ? 'five' : acts.length}`}</button></div>`
      + `<div id="cmp-leaders" class="calendar cl-grid">${whoLeadsHtml(list, dates, acts, { curIdx: i, activeAct: rated, open: ui.open })}</div>`
      + `</section>`
      + `<div class="cmp-grid">`
      + `<section class="dcard cg-temp"><div class="dcard-head"><span class="sec-title">${temp.title}</span><span class="sec-unit">feels-like, °C · ${ui.split.includes(temp.id) ? 'one chart per place, one scale' : `one axis, ${list.length === 3 ? 'three' : 'two'} places`}</span>${ui.split.includes(temp.id) ? '' : legendHtml(list)}${splitBtn(temp.id)}</div>`
      + chartBody(temp, true, MOBILE.matches ? 160 : 220) + `</section>`
      + (MOBILE.matches ? '' : smallCards)
      + `<section class="dcard cg-dots"><div class="dcard-head cg-dots-head"><span class="sum-eyebrow">${escapeHtml(when)}, every reading</span>`
      + `<span class="cmp-seg"><button type="button" data-dots="dots" class="${ui.table ? '' : 'on'}">dots</button><button type="button" data-dots="table" class="${ui.table ? 'on' : ''}">table</button></span></div>`
      + readings + `</section>`
      + `</div>`;
  } else {
    const rated = activeRating();
    body = lead
      + `<div class="cm-heads" style="grid-template-columns:repeat(${list.length},minmax(0,1fr))">${mastheadsHtml(list, v, i, state.recipe)}</div>`
      + `<section class="cmp-sec"><div class="dcard-head"><span class="sec-title">When each place is good</span><span class="sec-unit">${acts.length} ratings · a strip per place · thirty-six ten-day sections</span></div>`
      + `<div class="calendar cg-grid">${goodWhenHtml(list, dates, acts, { curIdx: i })}</div></section>`
      + `<div class="cmp-two">`
      + `<section><div class="dcard-head"><span class="sec-title">${escapeHtml(when)}, head to head</span>${rated ? `<span class="sec-unit">best for ${escapeHtml(VAR[rated].label.toLowerCase())} lit</span>` : ''}</div>`
      + headToHeadHtml(list, v, i, dates) + `</section>`
      + `<section><div class="dcard-head"><span class="sec-title">Temperature</span><span class="sec-unit">one chart per place, one scale</span></div>`
      + `<div class="sbs-grid" style="grid-template-columns:repeat(${list.length},minmax(0,1fr))">${sideBySideCharts(list, 't', dates, i)}</div>`
      + `<div class="dcard-head sbs-next"><span class="sec-title sec-small">Chance of rain</span><span class="sec-unit">chance %</span></div>`
      + `<div class="sbs-grid" style="grid-template-columns:repeat(${list.length},minmax(0,1fr))">${sideBySideCharts(list, 'rain', dates, i)}</div>`
      + `</section></div>`;
  }
  $('cmpbody').innerHTML = `<div class="cmp-doc${state.compare.full ? ' cmp-full' : ''}">${body}</div>`;
}

// "Comparing Lisbon Split Palermo · at a glance", and the address line.
function renderSentenceNames(): void {
  const names = $('cmp-names');
  const places = state.compare.places;
  // Each name carries its country, small, inside the same control — "Palermo"
  // alone could be Italy's or Colombia's.
  const words = places.map((p, k) => `<button type="button" id="cmp-name-${k}" class="sn-word cmp-word cmp-c${k}" data-k="${k}" aria-haspopup="true">${escapeHtml(p.name)}<span class="cmp-word-where">${escapeHtml(whereOf(p))}</span></button>`).join(' ');
  const add = places.length < MAX_COMPARE ? ' <button type="button" id="cmp-add" class="ce-chip cmp-add">+ add a place</button>' : '';
  names.innerHTML = `${words}${add} <span class="sn-sep" aria-hidden="true">·</span> <button type="button" id="cmp-mode" class="sn-word" aria-haspopup="true">${state.compare.full ? 'in full' : 'at a glance'}</button>`;
  $('comparing').hidden = state.view !== 'compare';
  const path = comparePath({ places: places.map((p) => p.placeSlug), full: state.compare.full }).replace(/\/$/, '');
  $('sn-link-path').textContent = `${location.host}${path}`;
  renderCompareMenus();
}

// ── the two menus ────────────────────────────────────────────────────────────
export function renderCompareMenus(): void {
  const m = state.menu;
  const modeBox = $('compare-mode-menu');
  const placeBox = $('compare-place-menu');
  modeBox.hidden = m !== 'compare-mode';
  placeBox.hidden = m !== 'compare-place';
  document.querySelectorAll('#cmp-names .sn-word').forEach((el) => el.classList.remove('open'));
  if (m === 'compare-mode') {
    const glance = menuRow('At a glance', 'who leads · three lines', { active: !state.compare.full });
    glance.addEventListener('click', () => setFull(false));
    const full = menuRow('In full', 'every reading, side by side', { active: state.compare.full });
    full.addEventListener('click', () => setFull(true));
    const list = document.createElement('div');
    list.className = 'menu-list';
    list.append(glance, full);
    modeBox.replaceChildren(list);
    const word = $maybe('cmp-mode');
    if (word) { word.classList.add('open'); anchorTo(modeBox, word, $('cmp-names')); }
  }
  if (m === 'compare-place') {
    const p = state.compare.places[menuIdx];
    if (!p) { placeBox.hidden = true; return; }
    const list = document.createElement('div');
    list.className = 'menu-list';
    const open = menuRow(`Open ${p.name}’s briefing`, '');
    open.addEventListener('click', () => { store.set({ menu: null }); deps.openCity(p.id, p); });
    list.append(open);
    if (state.compare.places.length > 2) {
      const out = menuRow(`Take ${p.name} out`, '');
      out.addEventListener('click', () => removePlace(menuIdx));
      list.append(out);
    } else {
      const swap = menuRow(`Swap ${p.name} for another place`, '');
      swap.addEventListener('click', () => removePlace(menuIdx));
      list.append(swap);
    }
    placeBox.replaceChildren(list);
    const word = $maybe(`cmp-name-${menuIdx}`);
    if (word) { word.classList.add('open'); anchorTo(placeBox, word, $('cmp-names')); }
  }
}

function setFull(full: boolean): void {
  store.set({ compare: { places: state.compare.places, full }, menu: null });
  renderCompare();
  deps.persist();
}

// ── the tray ─────────────────────────────────────────────────────────────────
// A comparison waiting for its next place, while the reader looks for it.
export function renderTray(): void {
  const places = state.compare.places;
  const on = state.view !== 'compare' && state.view !== 'landing' && places.length >= 1;
  const tray = $('compare-tray');
  tray.hidden = !on;
  if (!on) return;
  $('tray-names').innerHTML = places.map((p, k) => `<span class="cmp-name cmp-c${k}">${escapeHtml(p.name)}</span> <span class="cmp-where">${escapeHtml(whereOf(p))}</span>`).join('<span class="tray-sep">·</span>');
  $('tray-more').hidden = places.length >= MAX_COMPARE;
  $<HTMLButtonElement>('tray-go').disabled = places.length < 2;
}

// ── wiring ───────────────────────────────────────────────────────────────────
export function wireCompare(): void {
  // The sentence's words, delegated: #cmp-names is rebuilt on every render.
  $('cmp-names').addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    e.stopPropagation();
    if (t.closest('#cmp-mode')) {
      store.set({ menu: state.menu === 'compare-mode' ? null : 'compare-mode' });
    } else if (t.closest('#cmp-add')) {
      startCompareFrom(null);
    } else {
      const w = t.closest<HTMLElement>('.cmp-word');
      if (!w) return;
      const k = Number(w.dataset.k);
      const same = state.menu === 'compare-place' && menuIdx === k;
      menuIdx = k;
      store.set({ menu: same ? null : 'compare-place' });
      if (!same) renderCompareMenus();
    }
  });
  $('sn-link-copy').addEventListener('click', () => deps.copyLink($('sn-link-copy')));

  // The "at a glance" disclosures. Delegated: the document is rebuilt from a
  // string on every date change.
  $('cmpbody').addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const ui = state.compareUi;
    const toggle = t.closest<HTMLElement>('[data-cl-toggle]');
    const all = t.closest<HTMLElement>('[data-cl-all]');
    const split = t.closest<HTMLElement>('[data-split]');
    const dots = t.closest<HTMLElement>('[data-dots]');
    let next = ui;
    if (toggle) {
      const a = toggle.dataset.clToggle!;
      next = { ...ui, open: ui.open.includes(a) ? ui.open.filter((x) => x !== a) : [...ui.open, a] };
    } else if (all) {
      next = { ...ui, open: all.dataset.clAll === 'open' ? deps.activityKeys().slice() : [] };
    } else if (split) {
      const id = split.dataset.split!;
      next = { ...ui, split: ui.split.includes(id) ? ui.split.filter((x) => x !== id) : [...ui.split, id] };
    } else if (dots) {
      next = { ...ui, table: dots.dataset.dots === 'table' };
    } else {
      return;
    }
    e.stopPropagation();
    store.set({ compareUi: next });
    renderCompare();
    deps.persist();
  });

  // A leader cell names a rating and a time: going there is scrubbing the page
  // to that dekad with that rating on show — the briefing calendar's own click.
  $('cmpbody').addEventListener('click', (e) => {
    const cell = e.target instanceof Element ? e.target.closest<HTMLElement>('.cl-cell') : null;
    if (!cell) return;
    const row = cell.closest<HTMLElement>('.cl-row');
    const i = Number(cell.dataset.i);
    if (!Number.isInteger(i)) return;
    deps.pause();
    deps.showFrame(i);
    if (row && row.dataset.act && VAR[row.dataset.act as VarKey]) deps.applyVariable(row.dataset.act as VarKey);
    deps.persist();
  });

  $('tray-more').addEventListener('click', (e) => { e.stopPropagation(); openSearch('compare'); });
  $('tray-go').addEventListener('click', () => openCompare(state.compare.places));
  $('tray-clear').addEventListener('click', () => {
    store.set({ compare: { places: [], full: state.compare.full } });
    renderTray();
  });

  // The page follows the app's own date, rating, recipe and time of day.
  store.subscribe(['dateIdx', 'variable', 'recipeCanon'], () => {
    if (state.view !== 'compare') return;
    if (loaded) for (const s of loaded) computeCustomSeries(s);
    renderCompare();
  });
  store.subscribe(['bucket'], () => { if (state.view === 'compare') load(); });
  store.subscribe(['menu'], renderCompareMenus);
  window.addEventListener('resize', () => { if (state.view === 'compare') { renderCompare(); } });
}

// The names, for a page title or a caption.
export function compareNames(): string {
  return listNames(state.compare.places.map((p) => p.name));
}
